// statistics_clinic_01b_practice_position_relationship.md — narrowed to
// Correlation & Scatterplot only. Position statistics (rank, quartiles,
// percentiles, IQR) moved to statisticsClinic03OutliersSamplingBias.js,
// since IQR is used directly there to build the outlier fence.

export const STATS_CLINIC_01B_TRACKER_ID = 'statistics-clinic-01b-practice-bank';

export const STATS_CLINIC_01B_LENSES = [
  ['business_question', 'Business question'],
  ['statistic_family', 'Statistic family'],
  ['statistic_chosen', 'Statistic chosen'],
  ['why_statistic', 'Why this statistic'],
  ['calculation', 'Calculation'],
  ['result', 'Result'],
  ['meaning', 'Meaning'],
  ['decision', 'Decision'],
  ['caution', 'Caution'],
];

export const STATS_CLINIC_01B_ITEMS = [
  {
    id: 'DIY01',
    title: 'DIY Practice 01: Campaign review',
    scenario: 'The marketing manager is reviewing a campaign dashboard. Clicks and sales both appear to rise across the sample, but she wants the analyst to check whether the two numeric fields move together before she claims the campaign is working. The final note must avoid overstating cause and effect.',
    tables: [{
      columns: ['campaign_id', 'clicks', 'sales'],
      rows: [['C001', '120', '8'], ['C002', '250', '17'], ['C003', '310', '21'], ['C004', '460', '30'], ['C005', '500', '33']],
    }],
    answerGuide: {
      business_question: 'Are clicks and sales moving together, and can the campaign be called a success on this evidence?',
      statistic_family: 'Relationship.',
      statistic_chosen: 'Correlation, supported by a scatter plot.',
      why_statistic: 'Both fields are numeric and the question is whether they move together, not whether one causes the other.',
      calculation: 'As clicks rise from 120 to 500, sales rise from 8 to 33 in the same order for every row; a scatter plot would show a clear upward slope; CORREL would be strongly positive.',
      result: 'Strong positive relationship in this sample.',
      meaning: 'Higher-click campaigns in this sample are associated with higher sales.',
      decision: 'Report the relationship as encouraging evidence, but recommend a controlled test before crediting the campaign with causing the sales increase.',
      caution: 'Correlation does not prove the campaign caused the sales rise — a third factor (season, other promotions) could explain both.',
    },
    ruleTerms: {
      business_question: ['clicks', 'sales', 'move together'],
      statistic_family: ['relationship'],
      statistic_chosen: ['correlation'],
      why_statistic: ['numeric', 'move together'],
      calculation: ['120', '500', '8', '33', 'positive'],
      result: ['strong positive'],
      meaning: ['associated'],
      decision: ['controlled test', 'cause'],
      caution: ['correlation', 'not', 'prove'],
    },
    criticalTerms: { caution: ['correlation'] },
  },
  {
    id: 'DIY02',
    title: 'DIY Practice 02: Discount claim',
    scenario: 'The commercial manager believes bigger discounts are producing bigger orders. The table does show discount and order value side by side, but the analyst has been asked to check the relationship first and write a cautious conclusion that does not pretend a simple calculation proves the reason customers bought more.',
    tables: [{
      columns: ['order_id', 'discount_pct', 'order_value'],
      rows: [['D001', '0', '120'], ['D002', '5', '140'], ['D003', '10', '180'], ['D004', '15', '190'], ['D005', '20', '240']],
    }],
    answerGuide: {
      business_question: 'Is there a relationship between discount and order value that supports the manager\'s belief, and can it be called causal?',
      statistic_family: 'Relationship.',
      statistic_chosen: 'Correlation, supported by a scatter plot.',
      why_statistic: 'Both fields are numeric and the question is whether they move together.',
      calculation: 'As discount_pct rises from 0 to 20, order_value rises from 120 to 240 in the same order for every row (though the D003-to-D004 rise is small); a scatter plot would show an overall upward trend; CORREL would be positive.',
      result: 'Positive relationship in this sample.',
      meaning: 'Bigger discounts are associated with bigger order values here, but the relationship is not perfectly smooth.',
      decision: 'Report a cautious positive relationship, not a proven cause — recommend testing discount levels in a controlled way before assuming discounts are driving order size.',
      caution: 'Correlation does not prove bigger discounts caused bigger orders — customers who were already going to buy more might simply be the ones offered bigger discounts.',
    },
    ruleTerms: {
      business_question: ['discount', 'order value', 'causal'],
      statistic_family: ['relationship'],
      statistic_chosen: ['correlation'],
      why_statistic: ['numeric', 'move together'],
      calculation: ['0', '20', '120', '240', 'positive'],
      result: ['positive'],
      meaning: ['associated', 'not', 'smooth'],
      decision: ['cautious', 'controlled test'],
      caution: ['correlation', 'not', 'prove', 'cause'],
    },
    criticalTerms: { caution: ['correlation'] },
  },
  {
    id: 'DIY03',
    title: 'DIY Practice 03: Training program review',
    scenario: 'A quality lead wants to know whether more training hours are associated with fewer packing errors, before deciding whether to expand the training program to the rest of the warehouse team.',
    tables: [{
      columns: ['employee_id', 'training_hours', 'error_rate_pct'],
      rows: [['T001', '2', '9'], ['T002', '4', '7'], ['T003', '6', '5'], ['T004', '8', '4'], ['T005', '10', '2']],
    }],
    answerGuide: {
      business_question: 'Are more training hours associated with fewer packing errors?',
      statistic_family: 'Relationship.',
      statistic_chosen: 'Correlation, supported by a scatter plot.',
      why_statistic: 'Both fields are numeric and the question is whether they move together — in this case, whether they move in opposite directions.',
      calculation: 'As training_hours rises from 2 to 10, error_rate falls from 9% to 2% in the same order for every row; a scatter plot would show a downward slope; CORREL would be strongly negative.',
      result: 'Strong negative relationship in this sample.',
      meaning: 'More training hours are associated with fewer packing errors.',
      decision: 'Consider expanding the training program, but confirm with a controlled trial before crediting training alone.',
      caution: 'Correlation does not prove training caused the error reduction — more experienced staff may have both more accumulated training hours and naturally fewer errors from tenure alone (a confounder).',
    },
    ruleTerms: {
      business_question: ['training hours', 'fewer errors'],
      statistic_family: ['relationship'],
      statistic_chosen: ['correlation'],
      why_statistic: ['numeric', 'opposite'],
      calculation: ['2', '10', '9', '2', 'negative'],
      result: ['strong negative'],
      meaning: ['more training', 'fewer errors'],
      decision: ['expand', 'controlled trial'],
      caution: ['confounder', 'tenure'],
    },
    criticalTerms: { result: ['negative'] },
  },
  {
    id: 'DIY04',
    title: 'DIY Practice 04: Call routing by tenure',
    scenario: 'A call centre manager wants to know whether agents with longer tenure achieve higher customer satisfaction scores, before deciding whether to weight tenure heavily when routing calls to agents.',
    tables: [{
      columns: ['agent_id', 'tenure_months', 'satisfaction_score'],
      rows: [['A01', '3', '4.1'], ['A02', '8', '3.8'], ['A03', '14', '4.3'], ['A04', '20', '3.9'], ['A05', '30', '4.0']],
    }],
    answerGuide: {
      business_question: 'Do agents with longer tenure achieve higher customer satisfaction scores?',
      statistic_family: 'Relationship.',
      statistic_chosen: 'Correlation, supported by a scatter plot.',
      why_statistic: 'Both fields are numeric and the question is whether they move together.',
      calculation: 'Mean tenure = 15 months, mean satisfaction = 4.02. Sum of products = -0.6, sum of squared dx = 444, sum of squared dy = 0.148. r = -0.6 / sqrt(444x0.148) = -0.6/8.11 = -0.07.',
      result: 'r is approximately -0.07 — negligible, per the strength band table.',
      meaning: 'Tenure and satisfaction scores do not meaningfully move together in this sample.',
      decision: 'Do not weight tenure heavily in call routing based on this evidence — look for other factors that might actually predict satisfaction.',
      caution: 'A negligible correlation is still a real, useful finding — it tells the manager tenure is not the lever to pull here, saving effort chasing a relationship that is not there.',
    },
    ruleTerms: {
      business_question: ['tenure', 'satisfaction'],
      statistic_family: ['relationship'],
      statistic_chosen: ['correlation'],
      why_statistic: ['numeric', 'move together'],
      calculation: ['15', '4.02', '-0.07', '0.07'],
      result: ['negligible', '-0.07', '0.07'],
      meaning: ['not', 'meaningfully'],
      decision: ['do not', 'other factors'],
      caution: ['real', 'useful', 'not the lever'],
    },
    criticalTerms: { result: ['negligible'] },
  },
];

export const STATS_CLINIC_01B_CONFIG = {
  trackerId: STATS_CLINIC_01B_TRACKER_ID,
  resourceKey: 'statistics_clinic_01b_practice_position_relationship',
  rubricKey: 'statistics_correlation_scatterplot',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Correlation & Scatterplot Cases',
  description: 'Each case is a business request. The statistic is deliberately not named in the title — read the scenario, identify what is really being asked, then show the maths. Your work autosaves to your own practice record.',
  introAlert: 'Always look at the scatter plot shape before trusting the correlation number, and never claim causation from correlation alone.',
  lenses: STATS_CLINIC_01B_LENSES,
  workingField: null,
  items: STATS_CLINIC_01B_ITEMS,
};
